// @greenlight-spec: 19-design-system.md
// @greenlight-spec: 42-app-marketplace.md
import * as React from 'react';
import { ArrowUpRight } from 'lucide-react';

import { AppCoverArt } from '@/components/greenlight/AppCoverArt';
import { AppIconTile } from '@/components/greenlight/AppIconTile';
import { MonoValue } from '@/components/greenlight/MonoValue';
import { OwnerAvatar } from '@/components/greenlight/OwnerAvatar';
import { Card } from '@/components/ui/card';
import { Tag } from '@/components/greenlight/Tag';
import { cn } from '@/lib/utils';
import { type MarketplaceApp } from '@greenlight/shared';

import { MarketplaceCardActions } from './MarketplaceCardActions';

interface MarketplaceAppCardProps {
  app: MarketplaceApp;
}

// Cover: the app's captured screenshot, or the branded cover art placeholder
// when none exists (docs/28 §Imagery). A parallel copy of AppGridCard's Cover:
// that card overlays status/pipeline chrome this consumer card never shows,
// so the two aren't a good fit for one shared component.
function Cover({ app }: { app: MarketplaceApp }): React.JSX.Element {
  const url = app.cover_screenshot_url;
  const [failed, setFailed] = React.useState(false);
  React.useEffect(() => setFailed(false), [url]);
  const showImage = url != null && url !== '' && !failed;

  return (
    <div className="relative aspect-[16/10] w-full overflow-hidden border-b border-border bg-muted/40">
      {showImage ? (
        <img
          src={url}
          alt=""
          aria-hidden
          loading="lazy"
          className="h-full w-full object-cover"
          onError={() => setFailed(true)}
        />
      ) : (
        <AppCoverArt name={app.name} slug={app.slug} iconUrl={app.icon_url} />
      )}
    </div>
  );
}

/**
 * Marketplace discovery card (docs/42 §/marketplace): consumer-facing —
 * icon/cover, name, owner, tags, summary. An accessible row's whole card is a
 * single Open link to the live deployment URL (mirroring AppGridCard's
 * whole-card-is-one-link pattern), not just a corner button; no
 * status/pipeline/Manage chrome (AppGridCard's job for the management /apps
 * grid). The marketplace lists only apps the viewer can open, so every card here
 * links out: there is no locked card, no blurred card, and nothing to request —
 * an app that is restricted to a named list is simply absent from this catalog.
 */
export function MarketplaceAppCard({ app }: MarketplaceAppCardProps): React.JSX.Element {
  const hasDeployment = app.deployment_url != null && app.deployment_url !== '';
  const cardClassName = 'group relative flex h-full flex-col overflow-hidden border-border/60 p-0';

  const body = (
    <>
      <Cover app={app} />
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div className="flex min-w-0 items-center gap-2.5">
          <AppIconTile iconUrl={app.icon_url} name={app.name} slug={app.slug} size={36} />
          <div className="flex min-w-0 flex-col gap-0.5">
            <span className="truncate text-[15px] font-semibold text-foreground">{app.name}</span>
            <MonoValue className="w-fit">{app.slug}</MonoValue>
          </div>
        </div>

        {app.owner_email && (
          <div className="flex min-w-0 items-center gap-1.5">
            <OwnerAvatar name={app.owner_name} email={app.owner_email} />
            <span className="min-w-0 truncate text-sm text-muted-foreground">
              {app.owner_email}
            </span>
          </div>
        )}

        {app.doc_summary && (
          <p className="line-clamp-2 text-[13px] leading-relaxed text-muted-foreground">
            {app.doc_summary}
          </p>
        )}

        {app.tags.length > 0 && (
          <ul className="flex flex-wrap items-center gap-1.5" aria-label="Tags">
            {app.tags.map((tag) => (
              <li key={tag}>
                <Tag>{tag}</Tag>
              </li>
            ))}
          </ul>
        )}

        {/* Decorative external-link cue (docs/19 external-link convention,
            e.g. the app-detail deploy-URL row) — the wrapping <a> carries the
            actual action, not this icon. Omitted on a bare (undeployed) card.
            Same h-11/md:h-8 row height as the restricted card's Request
            access button, so the arrow lands at the same vertical position
            across the grid regardless of which row a card renders. */}
        {hasDeployment && (
          <div className="mt-auto flex h-11 items-center justify-end md:h-8">
            <ArrowUpRight
              className="size-4 text-link transition-colors group-hover:text-link/80"
              aria-hidden
            />
          </div>
        )}
      </div>
    </>
  );

  // No deployment URL yet → nothing to open; render the bare card (no link,
  // no hover affordance) rather than a dead click target (docs/42 §/marketplace).
  if (!hasDeployment) {
    return (
      <Card className={cardClassName}>
        <MarketplaceCardActions app={app} />
        {body}
      </Card>
    );
  }

  return (
    <Card className={cn(cardClassName, 'transition-colors hover:border-border')}>
      <MarketplaceCardActions app={app} />
      <a
        href={app.deployment_url ?? undefined}
        target="_blank"
        rel="noreferrer"
        aria-label={`Open ${app.name}`}
        className="flex h-full flex-col rounded-[inherit] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
      >
        {body}
      </a>
    </Card>
  );
}
